/** Place-level printable layouts (thermal text / TSPL-oriented). */

export interface PrintFormatBlocks {
  header: boolean
  /** Kitchen / station ticket header: which station(s) the ticket is for (before optional custom header lines). */
  stationTitle: boolean
  orderMeta: boolean
  lineItems: boolean
  totals: boolean
  footer: boolean
}

export interface PrintFormatV1 {
  version: 1
  blocks: PrintFormatBlocks
  headerLines: string[]
  footerLines: string[]
  /** Kitchen ticket only — ignored by label format. */
  showWatermark?: boolean
  /** Reserved for future Mustache-style overrides */
  advancedTemplate?: string | null
}

export interface PrintFormatQrBlocks {
  tableLabel: boolean
  placeName: boolean
  callToAction: boolean
  header: boolean
  footer: boolean
  urlAsText: boolean
  /** Place branding logo printed above the QR (ESC/POS receipt printers). */
  placeLogo: boolean
}

export interface PrintFormatQrV1 {
  version: 1
  blocks: PrintFormatQrBlocks
  headerLines: string[]
  footerLines: string[]
  callToActionText: string
  /** ESC/POS QR module size (3–8). */
  escPosModuleSize: number
  /** TSPL QRCODE cell width argument (1–10). */
  tsplCellSize: number
}

export type QrSheetPerPage = 4 | 6 | 9
export type QrSheetLogoPlate = 'auto' | 'on' | 'off'

/** A4 sheet presentation. Text content stays on the thermal QR label format. */
export interface PrintFormatQrSheetV1 {
  version: 1
  pageBackground: string
  textColor: string
  logoPlate: QrSheetLogoPlate
  perPage: QrSheetPerPage
}

export type DocumentPageSize = 'a4' | 'letter' | 'a3'

export interface PrintFormatDocumentV1 {
  version: 1
  blocks: PrintFormatBlocks
  headerLines: string[]
  footerLines: string[]
  pageSize: DocumentPageSize
  /** Page margin in mm for HTML/PDF print. */
  marginMm: number
  advancedTemplate?: string | null
}

import type { PrintFormatDocumentV2 } from './documentInvoiceV2'
import {
  defaultPrintFormatDocumentV2,
  mergeFormatDocument,
  type PrintFormatDocument,
} from './documentInvoiceV2'
import {
  defaultPrintFormatReceiptV2,
  mergePrintFormatReceiptV2,
  migrateCustomerReceiptV1ToV2,
  type PrintFormatCustomerReceipt,
} from './customerReceiptV2'

export type { PrintFormatDocument, PrintFormatDocumentV2 } from './documentInvoiceV2'
export {
  applyDocumentInvoicePreset,
  defaultPrintFormatDocumentV2,
  migrateDocumentInvoiceV1ToV2,
  normalizeDocumentInvoice,
  isDocumentInvoiceV2,
} from './documentInvoiceV2'
export type { PrintFormatCustomerReceipt, PrintFormatReceiptV2 } from './customerReceiptV2'
export {
  defaultPrintFormatReceiptV2,
  migrateCustomerReceiptV1ToV2,
  normalizeCustomerReceipt,
  isCustomerReceiptV2,
  mergePrintFormatReceiptV2,
} from './customerReceiptV2'

export interface PrintFormatBundleV1 {
  version: 1
  customerReceipt: PrintFormatCustomerReceipt
  documentInvoice: PrintFormatDocument
  kitchenTicket: PrintFormatV1
  label: PrintFormatV1
  qrLabel: PrintFormatQrV1
  qrSheet: PrintFormatQrSheetV1
}

export function defaultPrintFormatV1(overrides?: Partial<PrintFormatV1>): PrintFormatV1 {
  const base: PrintFormatV1 = {
    version: 1,
    blocks: {
      header: true,
      stationTitle: false,
      orderMeta: true,
      lineItems: true,
      totals: false,
      footer: true,
    },
    headerLines: [],
    footerLines: [],
    showWatermark: false,
    advancedTemplate: null,
  }
  if (!overrides) return base
  return {
    ...base,
    ...overrides,
    blocks: { ...base.blocks, ...overrides.blocks },
    headerLines: overrides.headerLines ?? base.headerLines,
    footerLines: overrides.footerLines ?? base.footerLines,
    showWatermark: overrides.showWatermark ?? base.showWatermark,
  }
}

export function defaultPrintFormatQrV1(overrides?: Partial<PrintFormatQrV1>): PrintFormatQrV1 {
  const base: PrintFormatQrV1 = {
    version: 1,
    blocks: {
      tableLabel: false,
      placeName: false,
      callToAction: false,
      header: false,
      footer: false,
      urlAsText: false,
      placeLogo: false,
    },
    headerLines: [],
    footerLines: [],
    callToActionText: 'Scan to order',
    escPosModuleSize: 4,
    tsplCellSize: 4,
  }
  if (!overrides) return base
  return {
    ...base,
    ...overrides,
    blocks: { ...base.blocks, ...overrides.blocks },
    headerLines: overrides.headerLines ?? base.headerLines,
    footerLines: overrides.footerLines ?? base.footerLines,
    callToActionText: overrides.callToActionText ?? base.callToActionText,
    escPosModuleSize: overrides.escPosModuleSize ?? base.escPosModuleSize,
    tsplCellSize: overrides.tsplCellSize ?? base.tsplCellSize,
  }
}

export function defaultPrintFormatQrSheetV1(
  overrides?: Partial<PrintFormatQrSheetV1>,
): PrintFormatQrSheetV1 {
  const base: PrintFormatQrSheetV1 = {
    version: 1,
    pageBackground: '#ffffff',
    textColor: '#111827',
    logoPlate: 'auto',
    perPage: 6,
  }
  if (!overrides) return base
  return { ...base, ...overrides, version: 1 }
}

export function defaultPrintFormatDocumentV1(
  overrides?: Partial<PrintFormatDocumentV1>,
): PrintFormatDocumentV1 {
  const base: PrintFormatDocumentV1 = {
    version: 1,
    blocks: {
      header: true,
      stationTitle: false,
      orderMeta: true,
      lineItems: true,
      totals: true,
      footer: true,
    },
    headerLines: [],
    footerLines: [],
    pageSize: 'a4',
    marginMm: 12,
    advancedTemplate: null,
  }
  if (!overrides) return base
  return {
    ...base,
    ...overrides,
    blocks: { ...base.blocks, ...overrides.blocks },
    headerLines: overrides.headerLines ?? base.headerLines,
    footerLines: overrides.footerLines ?? base.footerLines,
    pageSize:
      overrides.pageSize === 'a4' || overrides.pageSize === 'letter' || overrides.pageSize === 'a3'
        ? overrides.pageSize
        : base.pageSize,
    marginMm:
      typeof overrides.marginMm === 'number' && overrides.marginMm >= 0
        ? overrides.marginMm
        : base.marginMm,
  }
}

export function defaultPrintFormatBundle(): PrintFormatBundleV1 {
  return {
    version: 1,
    customerReceipt: defaultPrintFormatReceiptV2(),
    documentInvoice: defaultPrintFormatDocumentV2(),
    kitchenTicket: defaultPrintFormatV1({
      blocks: {
        header: true,
        stationTitle: true,
        orderMeta: true,
        lineItems: true,
        totals: false,
        footer: true,
      },
    }),
    label: defaultPrintFormatV1({
      blocks: {
        header: true,
        stationTitle: false,
        orderMeta: true,
        lineItems: true,
        totals: false,
        footer: false,
      },
    }),
    qrLabel: defaultPrintFormatQrV1(),
    qrSheet: defaultPrintFormatQrSheetV1(),
  }
}

/** Merges saved blocks; older settings may omit `stationTitle`. */
function mergeBlocksFromRaw(def: PrintFormatBlocks, raw: unknown): PrintFormatBlocks {
  if (!raw || typeof raw !== 'object') return def
  const b = raw as Partial<PrintFormatBlocks>
  if (
    typeof b.header !== 'boolean' ||
    typeof b.orderMeta !== 'boolean' ||
    typeof b.lineItems !== 'boolean' ||
    typeof b.totals !== 'boolean' ||
    typeof b.footer !== 'boolean'
  ) {
    return def
  }
  return {
    ...def,
    header: b.header,
    orderMeta: b.orderMeta,
    lineItems: b.lineItems,
    totals: b.totals,
    footer: b.footer,
    stationTitle: typeof b.stationTitle === 'boolean' ? b.stationTitle : def.stationTitle,
  }
}

function clampInt(n: unknown, min: number, max: number, fallback: number): number {
  const v = typeof n === 'number' ? n : typeof n === 'string' ? parseInt(n, 10) : NaN
  if (!Number.isFinite(v)) return fallback
  return Math.min(max, Math.max(min, Math.floor(v)))
}

function mergeQrBlocksFromRaw(def: PrintFormatQrBlocks, raw: unknown): PrintFormatQrBlocks {
  if (!raw || typeof raw !== 'object') return def
  const b = raw as Partial<PrintFormatQrBlocks>
  return {
    tableLabel: typeof b.tableLabel === 'boolean' ? b.tableLabel : def.tableLabel,
    placeName: typeof b.placeName === 'boolean' ? b.placeName : def.placeName,
    callToAction: typeof b.callToAction === 'boolean' ? b.callToAction : def.callToAction,
    header: typeof b.header === 'boolean' ? b.header : def.header,
    footer: typeof b.footer === 'boolean' ? b.footer : def.footer,
    urlAsText: typeof b.urlAsText === 'boolean' ? b.urlAsText : def.urlAsText,
    placeLogo: typeof b.placeLogo === 'boolean' ? b.placeLogo : def.placeLogo,
  }
}

function hexColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback
  const v = value.trim()
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : fallback
}

function mergeFormatQrSheet(def: PrintFormatQrSheetV1, raw: unknown): PrintFormatQrSheetV1 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<PrintFormatQrSheetV1>
  const perPage = r.perPage === 4 || r.perPage === 6 || r.perPage === 9 ? r.perPage : def.perPage
  const logoPlate = r.logoPlate === 'auto' || r.logoPlate === 'on' || r.logoPlate === 'off' ? r.logoPlate : def.logoPlate
  return {
    version: 1,
    pageBackground: hexColor(r.pageBackground, def.pageBackground),
    textColor: hexColor(r.textColor, def.textColor),
    logoPlate,
    perPage,
  }
}

function mergeFormatQrV1(def: PrintFormatQrV1, raw: unknown): PrintFormatQrV1 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<PrintFormatQrV1>
  const blocks = mergeQrBlocksFromRaw(def.blocks, r.blocks)
  return {
    version: 1,
    blocks,
    headerLines: Array.isArray(r.headerLines)
      ? r.headerLines.filter((x): x is string => typeof x === 'string')
      : def.headerLines,
    footerLines: Array.isArray(r.footerLines)
      ? r.footerLines.filter((x): x is string => typeof x === 'string')
      : def.footerLines,
    callToActionText:
      typeof r.callToActionText === 'string' && r.callToActionText.trim()
        ? r.callToActionText.trim()
        : def.callToActionText,
    escPosModuleSize: clampInt(r.escPosModuleSize, 3, 8, def.escPosModuleSize),
    tsplCellSize: clampInt(r.tsplCellSize, 1, 10, def.tsplCellSize),
  }
}

function mergeFormatV1(def: PrintFormatV1, raw: unknown): PrintFormatV1 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<PrintFormatV1>
  const blocks = mergeBlocksFromRaw(def.blocks, r.blocks)
  return {
    version: 1,
    blocks,
    headerLines: Array.isArray(r.headerLines)
      ? r.headerLines.filter((x): x is string => typeof x === 'string')
      : def.headerLines,
    footerLines: Array.isArray(r.footerLines)
      ? r.footerLines.filter((x): x is string => typeof x === 'string')
      : def.footerLines,
    showWatermark:
      typeof r.showWatermark === 'boolean' ? r.showWatermark : def.showWatermark,
    advancedTemplate:
      r.advancedTemplate === undefined ? def.advancedTemplate : r.advancedTemplate,
  }
}

function mergeCustomerReceipt(raw: unknown): PrintFormatCustomerReceipt {
  const defV2 = defaultPrintFormatReceiptV2()
  if (!raw || typeof raw !== 'object') return defV2
  const r = raw as { version?: number }
  if (r.version === 2) return mergePrintFormatReceiptV2(defV2, raw)
  const defV1 = defaultPrintFormatV1({
    blocks: {
      header: true,
      stationTitle: false,
      orderMeta: true,
      lineItems: true,
      totals: true,
      footer: true,
    },
  })
  const v1 = mergeFormatV1(defV1, raw)
  return migrateCustomerReceiptV1ToV2(v1)
}

export function mergePrintFormatBundle(raw: unknown): PrintFormatBundleV1 {
  const def = defaultPrintFormatBundle()
  if (!raw || typeof raw !== 'object') return def
  const o = raw as Partial<PrintFormatBundleV1>
  if (o.version !== 1) return def
  return {
    version: 1,
    customerReceipt: mergeCustomerReceipt(o.customerReceipt),
    documentInvoice: mergeFormatDocument(
      def.documentInvoice as PrintFormatDocumentV2,
      o.documentInvoice,
    ),
    kitchenTicket: mergeFormatV1(def.kitchenTicket, o.kitchenTicket),
    label: mergeFormatV1(def.label, o.label),
    qrLabel: mergeFormatQrV1(def.qrLabel, o.qrLabel),
    qrSheet: mergeFormatQrSheet(def.qrSheet, o.qrSheet),
  }
}
